export interface CourseTemplate {
  id: string;
  title: string;
  niche: string;
  description: string;
  targetAudience: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedDuration: string;
  thumbnail: string;
  modules: Array<{
    title: string;
    description: string;
    lessons: Array<{
      title: string;
      description: string;
      duration: number;
      contentType: string;
    }>;
  }>;
}

export const courseTemplates: CourseTemplate[] = [
  {
    id: 'digital-marketing-fundamentals',
    title: 'Digital Marketing Fundamentals',
    niche: 'Marketing',
    description: 'Master the essential skills of digital marketing including SEO, social media, email campaigns, and analytics.',
    targetAudience: 'Small business owners, marketing beginners, entrepreneurs',
    difficulty: 'beginner',
    estimatedDuration: '4-6 hours',
    thumbnail: '📱',
    modules: [
      {
        title: 'Introduction to Digital Marketing',
        description: 'Understanding the digital marketing landscape and key concepts',
        lessons: [
          { title: 'What is Digital Marketing?', description: 'Overview of digital marketing channels', duration: 15, contentType: 'video' },
          { title: 'Digital Marketing vs Traditional Marketing', description: 'Key differences and advantages', duration: 20, contentType: 'video' },
          { title: 'Creating Your Marketing Strategy', description: 'Planning your digital presence', duration: 25, contentType: 'video' }
        ]
      },
      {
        title: 'Search Engine Optimization (SEO)',
        description: 'Learn how to rank higher in search results',
        lessons: [
          { title: 'SEO Basics', description: 'Understanding how search engines work', duration: 30, contentType: 'video' },
          { title: 'Keyword Research', description: 'Finding the right keywords for your business', duration: 25, contentType: 'video' },
          { title: 'On-Page SEO Techniques', description: 'Optimizing your website content', duration: 35, contentType: 'video' }
        ]
      },
      {
        title: 'Social Media Marketing',
        description: 'Building and engaging your audience on social platforms',
        lessons: [
          { title: 'Choosing the Right Platforms', description: 'Where your audience hangs out', duration: 20, contentType: 'video' },
          { title: 'Content Strategy for Social Media', description: 'Creating engaging posts', duration: 30, contentType: 'video' },
          { title: 'Social Media Advertising', description: 'Running effective paid campaigns', duration: 35, contentType: 'video' }
        ]
      },
      {
        title: 'Email Marketing',
        description: 'Build and nurture your email list',
        lessons: [
          { title: 'Building Your Email List', description: 'Lead magnets and opt-in forms', duration: 25, contentType: 'video' },
          { title: 'Writing High-Converting Emails', description: 'Copywriting for email', duration: 30, contentType: 'video' },
          { title: 'Email Automation', description: 'Setting up automated sequences', duration: 35, contentType: 'video' }
        ]
      }
    ]
  },
  {
    id: 'web-development-bootcamp',
    title: 'Complete Web Development Bootcamp',
    niche: 'Programming',
    description: 'Learn to build modern websites from scratch using HTML, CSS, JavaScript, and React.',
    targetAudience: 'Aspiring developers, career changers, coding beginners',
    difficulty: 'beginner',
    estimatedDuration: '8-10 hours',
    thumbnail: '💻',
    modules: [
      {
        title: 'HTML & CSS Fundamentals',
        description: 'Build the structure and style of websites',
        lessons: [
          { title: 'HTML Basics', description: 'Tags, elements, and structure', duration: 45, contentType: 'video' },
          { title: 'CSS Styling', description: 'Colors, fonts, and layouts', duration: 50, contentType: 'video' },
          { title: 'Responsive Design', description: 'Making sites mobile-friendly', duration: 40, contentType: 'video' }
        ]
      },
      {
        title: 'JavaScript Programming',
        description: 'Add interactivity to your websites',
        lessons: [
          { title: 'JavaScript Basics', description: 'Variables, functions, and logic', duration: 60, contentType: 'video' },
          { title: 'DOM Manipulation', description: 'Changing HTML with JavaScript', duration: 55, contentType: 'video' },
          { title: 'ES6+ Modern JavaScript', description: 'Arrow functions, promises, and more', duration: 50, contentType: 'video' }
        ]
      },
      {
        title: 'React Framework',
        description: 'Build modern single-page applications',
        lessons: [
          { title: 'Introduction to React', description: 'Components and JSX', duration: 45, contentType: 'video' },
          { title: 'State and Props', description: 'Managing data in React', duration: 50, contentType: 'video' },
          { title: 'Building a React App', description: 'Complete project walkthrough', duration: 90, contentType: 'video' }
        ]
      }
    ]
  },
  {
    id: 'personal-finance-mastery',
    title: 'Personal Finance Mastery',
    niche: 'Finance',
    description: 'Take control of your money with budgeting, investing, and wealth-building strategies.',
    targetAudience: 'Young professionals, anyone wanting financial freedom',
    difficulty: 'beginner',
    estimatedDuration: '3-4 hours',
    thumbnail: '💰',
    modules: [
      {
        title: 'Budgeting Basics',
        description: 'Create and stick to a budget that works',
        lessons: [
          { title: 'Understanding Your Income and Expenses', description: 'Tracking where your money goes', duration: 20, contentType: 'video' },
          { title: 'Creating Your First Budget', description: 'Step-by-step budget setup', duration: 25, contentType: 'video' },
          { title: 'Budgeting Apps and Tools', description: 'Technology to help you succeed', duration: 15, contentType: 'video' }
        ]
      },
      {
        title: 'Debt Management',
        description: 'Strategies to eliminate debt faster',
        lessons: [
          { title: 'Understanding Different Types of Debt', description: 'Good debt vs bad debt', duration: 20, contentType: 'video' },
          { title: 'Debt Payoff Strategies', description: 'Snowball vs avalanche method', duration: 25, contentType: 'video' },
          { title: 'Staying Debt-Free', description: 'Building healthy financial habits', duration: 20, contentType: 'video' }
        ]
      },
      {
        title: 'Investing for Beginners',
        description: 'Start building wealth through investments',
        lessons: [
          { title: 'Investment Basics', description: 'Stocks, bonds, and funds', duration: 30, contentType: 'video' },
          { title: 'Starting Your Investment Portfolio', description: 'Opening accounts and first investments', duration: 35, contentType: 'video' },
          { title: 'Long-Term Wealth Building', description: 'Retirement accounts and strategies', duration: 40, contentType: 'video' }
        ]
      }
    ]
  },
  {
    id: 'content-creation-mastery',
    title: 'Content Creation Mastery',
    niche: 'Content Creation',
    description: 'Learn to create engaging content for YouTube, TikTok, Instagram, and blogs.',
    targetAudience: 'Aspiring influencers, content creators, entrepreneurs',
    difficulty: 'beginner',
    estimatedDuration: '5-7 hours',
    thumbnail: '📹',
    modules: [
      {
        title: 'Finding Your Niche',
        description: 'Discover what content you should create',
        lessons: [
          { title: 'Identifying Your Passion and Skills', description: 'What makes you unique', duration: 25, contentType: 'video' },
          { title: 'Market Research', description: 'Finding your target audience', duration: 30, contentType: 'video' },
          { title: 'Choosing Your Platform', description: 'YouTube vs Instagram vs TikTok', duration: 25, contentType: 'video' }
        ]
      },
      {
        title: 'Content Planning and Strategy',
        description: 'Plan content that gets views and engagement',
        lessons: [
          { title: 'Content Ideas and Brainstorming', description: 'Never run out of content ideas', duration: 30, contentType: 'video' },
          { title: 'Creating a Content Calendar', description: 'Stay consistent with planning', duration: 25, contentType: 'video' },
          { title: 'Analyzing Trends', description: 'Jump on viral opportunities', duration: 20, contentType: 'video' }
        ]
      },
      {
        title: 'Video Production Basics',
        description: 'Film and edit professional-looking videos',
        lessons: [
          { title: 'Equipment for Beginners', description: 'Camera, lighting, and audio', duration: 35, contentType: 'video' },
          { title: 'Filming Techniques', description: 'Framing, lighting, and more', duration: 40, contentType: 'video' },
          { title: 'Video Editing Essentials', description: 'Using editing software effectively', duration: 60, contentType: 'video' }
        ]
      },
      {
        title: 'Growing Your Audience',
        description: 'Build a loyal following and monetize',
        lessons: [
          { title: 'SEO for Content', description: 'Get discovered in search', duration: 30, contentType: 'video' },
          { title: 'Engagement Strategies', description: 'Building community around your content', duration: 25, contentType: 'video' },
          { title: 'Monetization Options', description: 'Ads, sponsorships, and products', duration: 35, contentType: 'video' }
        ]
      }
    ]
  },
  {
    id: 'productivity-time-management',
    title: 'Productivity & Time Management',
    niche: 'Personal Development',
    description: 'Master proven techniques to get more done in less time and achieve your goals.',
    targetAudience: 'Busy professionals, students, entrepreneurs',
    difficulty: 'beginner',
    estimatedDuration: '3-4 hours',
    thumbnail: '⏰',
    modules: [
      {
        title: 'Productivity Fundamentals',
        description: 'Understanding how to work smarter, not harder',
        lessons: [
          { title: 'The Science of Productivity', description: 'How your brain works and focus', duration: 20, contentType: 'video' },
          { title: 'Common Productivity Myths', description: 'What doesn\'t actually work', duration: 15, contentType: 'video' },
          { title: 'Setting SMART Goals', description: 'Goals that actually get achieved', duration: 25, contentType: 'video' }
        ]
      },
      {
        title: 'Time Management Techniques',
        description: 'Proven methods to manage your time effectively',
        lessons: [
          { title: 'The Pomodoro Technique', description: 'Work in focused intervals', duration: 20, contentType: 'video' },
          { title: 'Time Blocking', description: 'Schedule your entire day', duration: 25, contentType: 'video' },
          { title: 'Eisenhower Matrix', description: 'Prioritize what really matters', duration: 20, contentType: 'video' }
        ]
      },
      {
        title: 'Eliminating Distractions',
        description: 'Create an environment for deep work',
        lessons: [
          { title: 'Digital Minimalism', description: 'Taming your devices', duration: 25, contentType: 'video' },
          { title: 'Creating Your Ideal Workspace', description: 'Environment design for focus', duration: 20, contentType: 'video' },
          { title: 'Managing Interruptions', description: 'Protecting your focused time', duration: 20, contentType: 'video' }
        ]
      },
      {
        title: 'Building Productive Habits',
        description: 'Make productivity your default mode',
        lessons: [
          { title: 'Morning Routines', description: 'Start your day right', duration: 25, contentType: 'video' },
          { title: 'Habit Stacking', description: 'Building on existing habits', duration: 20, contentType: 'video' },
          { title: 'Weekly Reviews', description: 'Reflect and improve continuously', duration: 25, contentType: 'video' }
        ]
      }
    ]
  },
  {
    id: 'ecommerce-shopify',
    title: 'E-commerce with Shopify',
    niche: 'E-commerce',
    description: 'Build and launch your own online store using Shopify from scratch to first sale.',
    targetAudience: 'Aspiring e-commerce entrepreneurs, online store owners',
    difficulty: 'beginner',
    estimatedDuration: '6-8 hours',
    thumbnail: '🛒',
    modules: [
      {
        title: 'E-commerce Fundamentals',
        description: 'Understanding online retail basics',
        lessons: [
          { title: 'Introduction to E-commerce', description: 'Different business models', duration: 20, contentType: 'video' },
          { title: 'Choosing Your Products', description: 'Product research and selection', duration: 35, contentType: 'video' },
          { title: 'Finding Suppliers', description: 'Dropshipping vs inventory', duration: 30, contentType: 'video' }
        ]
      },
      {
        title: 'Setting Up Your Shopify Store',
        description: 'Build your professional online store',
        lessons: [
          { title: 'Getting Started with Shopify', description: 'Account setup and basics', duration: 25, contentType: 'video' },
          { title: 'Choosing and Customizing Your Theme', description: 'Make your store look professional', duration: 45, contentType: 'video' },
          { title: 'Adding Products', description: 'Product listings that sell', duration: 35, contentType: 'video' }
        ]
      },
      {
        title: 'Marketing Your Store',
        description: 'Drive traffic and make sales',
        lessons: [
          { title: 'Facebook and Instagram Ads', description: 'Paid advertising for beginners', duration: 45, contentType: 'video' },
          { title: 'Email Marketing for E-commerce', description: 'Building and nurturing customers', duration: 35, contentType: 'video' },
          { title: 'Influencer Marketing', description: 'Leveraging social proof', duration: 30, contentType: 'video' }
        ]
      },
      {
        title: 'Scaling Your Business',
        description: 'Grow from first sale to consistent revenue',
        lessons: [
          { title: 'Analyzing Your Data', description: 'Using Shopify analytics', duration: 30, contentType: 'video' },
          { title: 'Optimizing for Conversions', description: 'Increase your sales rate', duration: 35, contentType: 'video' },
          { title: 'Customer Service Excellence', description: 'Building loyal customers', duration: 25, contentType: 'video' }
        ]
      }
    ]
  }
];
